import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

interface HeroSample {
  text: string;
  score: number;
  label: string;
  color: string;
}

interface BoardRow {
  rank: string;
  name: string;
}

interface FeatLens {
  pill: string;
  title: string;
  body: string;
}

interface FeatDocSource {
  text: string;
  score: number;
  kind: 'human' | 'humanized' | 'para';
  orig?: string;
}

interface FeatDocView {
  text: string;
  orig: string;
  showOrig: boolean;
  showTag: boolean;
  tag: string;
  ai: boolean;
  hl: boolean;
  border: string;
  bg: string;
}

interface AudPara {
  text: string;
  score: number;
}

interface AudRole {
  name: string;
  tag: string;
  body: string;
  uses: string[];
  cta: string;
  alt: string;
  file: string;
  docTitle: string;
  paras: AudPara[];
  score: number;
  verdict: string;
}

interface HowStep {
  title: string;
  body: string;
  callout: string;
}

@Component({
  selector: 'app-ai-detector-page',
  templateUrl: './ai-detector-page.component.html',
  styleUrls: ['./ai-detector-page.component.scss']
})
export class AiDetectorPageComponent implements OnInit {

  readonly trustLogos: string[] = ['[Princeton]', '[MIT]', '[IEEE]', '[Stanford]', '[Wiley]', '[Pfizer]', '[Abbott]'];

  readonly boardRows: BoardRow[] = [
    { rank: '#8', name: 'Grammarly' },
    { rank: '#11', name: 'QuillBot' },
    { rank: '#20', name: 'GPTZero' }
  ];

  readonly heroExampleLabels: string[] = ['AI-written', 'Humanized AI', 'Human-written'];

  private readonly heroTypedWords: string[] = ['humanized text', 'ChatGPT, Claude & more', 'paraphrased AI text', 'AI-edited writing'];

  private readonly heroSamples: HeroSample[] = [
    {
      text: 'Artificial intelligence has fundamentally transformed the landscape of modern research. By leveraging advanced algorithms, researchers can now analyze vast datasets with unprecedented efficiency. Furthermore, these tools facilitate collaboration across disciplines, fostering innovation and accelerating discovery.',
      score: 94, label: 'Confident AI', color: '#DC2626'
    },
    {
      text: 'AI has really changed how modern research gets done. With smart algorithms, researchers can now go through huge datasets much faster than before. On top of that, these tools help people from different fields work together, which sparks new ideas.',
      score: 70, label: 'Leaning AI', color: '#F59E0B'
    },
    {
      text: 'We surveyed 214 nurses across three district hospitals in Kerala between March and June 2025. Most respondents said night shifts, not workload, drove their intention to leave. We were surprised that pay ranked fifth.',
      score: 6, label: 'Confident human', color: '#16A34A'
    }
  ];

  readonly techniques: string[] = ['synonym substitution', 'whitespace and homoglyph injection', 'paraphrasing', 'article deletion', 'case manipulation'];

  readonly lenses: FeatLens[] = [
    { pill: '01  Humanized text', title: 'Detects Humanized Text', body: 'Tested specifically against "humanizing" tools (software built to disguise AI writing as human).' },
    { pill: '02  Verified accuracy', title: 'Verified Accuracy', body: 'Verified independently. Our [Top 3] ranking comes from RAID, the leading third-party benchmark for AI-text detection.' },
    { pill: '03  Overall score', title: 'Overall Level Score of Detection', body: 'See an overall AI-likelihood score for your document, then drill down to the paragraph level to pinpoint exactly where AI-generated text shows up.' },
    { pill: '04  Paraphrased content', title: 'Paraphrased Content Detection', body: 'Identifies AI-generated text, even when the text is paraphrased or stylistically altered.' },
    { pill: '05  Major LLMs', title: 'Detects All Major LLMs', body: 'Detects AI-generated text from LLMs such as ChatGPT, Gemini, Llama, Claude, and more.' },
    { pill: '06  PDF report', title: 'Downloadable PDF Report', body: 'Get an annotated PDF report that maintains your document structure.' }
  ];

  readonly models: string[] = ['ChatGPT', 'Gemini', 'Claude', 'Llama'];

  private readonly featDocSource: FeatDocSource[] = [
    { text: 'We surveyed 214 nurses across three district hospitals between March and June 2025. Most respondents said night shifts, not workload, drove their intention to leave.', score: 6, kind: 'human' },
    { text: 'Honestly, staffing has kind of become the whole story here. Hospitals can now track shifts way better than before, and it gets managers and nurses actually talking.', score: 88, kind: 'humanized' },
    { text: 'These findings help people across departments work together, which sparks better scheduling ideas.', orig: 'These findings facilitate cross-departmental collaboration, fostering innovative scheduling solutions.', score: 84, kind: 'para' },
    { text: 'We were surprised that pay ranked fifth.', score: 4, kind: 'human' }
  ];

  readonly roles: AudRole[] = [
    {
      name: 'Students & Researchers', tag: 'Assignments, theses, papers',
      body: 'Safeguards the integrity of your assignments, theses, and research publications, and ensures the authenticity and reliability of academic sources and reference materials.',
      uses: ['Assignments and theses before you share them', 'Research papers and drafts', 'Sources and reference material you rely on'],
      cta: 'Try for free', alt: '', file: 'Thesis_Chapter3.docx', docTitle: 'Chapter 3: Methodology',
      paras: [
        { text: 'We collected survey data from 214 nurses across three district hospitals between March and June 2025.', score: 3 },
        { text: 'Participants were recruited through ward supervisors and completed the questionnaire in person.', score: 6 },
        { text: 'Responses were coded independently by two researchers and disagreements resolved by discussion.', score: 4 }
      ],
      score: 4, verdict: 'Likely human'
    },
    {
      name: 'Professors & Mentors', tag: 'Coursework and student writing',
      body: "Verify whether students' academic work is genuinely human-created or AI-written, to maintain academic standards.",
      uses: ['Essays and coursework', 'Dissertation chapters you supervise', 'Writing samples across a whole class'],
      cta: 'Try for free', alt: 'For classrooms, see DocuMark', file: 'Essay_Week6_Student14.docx', docTitle: 'The Ethics of Gene Editing',
      paras: [
        { text: 'Gene editing represents a transformative frontier in modern medicine, offering unprecedented opportunities.', score: 92 },
        { text: 'Furthermore, it raises profound ethical questions that society must carefully navigate.', score: 88 },
        { text: 'My grandmother had sickle cell, so this debate feels personal to me.', score: 7 }
      ],
      score: 71, verdict: 'Leaning AI'
    },
    {
      name: 'Universities & Institutions', tag: 'Policy across departments',
      body: 'Uphold institutional guidelines by identifying AI-generated content in academic work. Promote integrity and trust in educational processes with our robust AI Detector.',
      uses: ['Academic work across departments', 'Integrity reviews and appeals', 'Institution-wide guidelines on AI use'],
      cta: 'Talk to us', alt: 'See DocuMark for institutions', file: 'Dept_Review_Q3 / Case_118.pdf', docTitle: 'Case 118: Term paper review',
      paras: [
        { text: 'The literature on urban heat islands has grown considerably over the past decade.', score: 18 },
        { text: 'In conclusion, these multifaceted dynamics underscore the pivotal importance of holistic approaches.', score: 94 },
        { text: 'Our own measurements in Pune showed a smaller effect than we expected.', score: 9 }
      ],
      score: 38, verdict: 'Mixed, review advised'
    },
    {
      name: 'Enterprises', tag: 'Reports and communications',
      body: 'Protect your corporate content, including reports and all communications, and make sure they are authentic and original.',
      uses: ['Client reports and proposals', 'Internal and external communications', 'Sensitive documents, in Confidential mode'],
      cta: 'Try for free', alt: 'Explore the Confidential Data Plan', file: 'Client_Proposal_v4.pdf', docTitle: 'Proposal: Q1 rollout plan',
      paras: [
        { text: 'We recommend phasing the rollout across two regions, starting with the western sales team in January.', score: 8 },
        { text: 'Budget figures below reflect the revised vendor quote from 12 October.', score: 5 },
        { text: 'Training will be delivered on-site by our implementation lead.', score: 11 }
      ],
      score: 12, verdict: 'Likely human'
    },
    {
      name: 'Writers & Bloggers', tag: 'Blogs, articles, posts',
      body: 'Avoid search engine penalties for AI-generated content by maintaining originality in your blogs, articles, and posts with the help of our AI Detector.',
      uses: ['Blog posts before you publish', 'Articles from freelancers', 'Social and newsletter copy'],
      cta: 'Try for free', alt: '', file: 'Blog_Draft_Monsoon.docx', docTitle: 'What a Mumbai monsoon taught me about deadlines',
      paras: [
        { text: 'The first proper rain arrived on a Tuesday, about four hours before my edit was due.', score: 5 },
        { text: 'I lost power twice and wrote the last section on my phone.', score: 3 },
        { text: 'Somewhere between those outages I worked out what the piece was actually about.', score: 9 }
      ],
      score: 6, verdict: 'Likely human'
    },
    {
      name: 'Journalists & Authors', tag: 'News, stories, books',
      body: 'Preserve the authenticity of news stories, articles, and books. Detect AI-generated content to maintain your voice and guarantee trustworthy, original narratives.',
      uses: ['News stories and features', 'Contributor and wire copy', 'Book manuscripts and chapters'],
      cta: 'Try for free', alt: '', file: 'Feature_Story_Final.docx', docTitle: 'Inside the city water board',
      paras: [
        { text: 'The board met for three hours on Thursday and adjourned without a vote.', score: 6 },
        { text: 'This pivotal moment underscores the multifaceted challenges facing urban water governance today.', score: 95 },
        { text: 'Residents in Ward 9 say tankers still arrive twice a week.', score: 4 }
      ],
      score: 91, verdict: 'Confident AI'
    }
  ];

  readonly howSteps: HowStep[] = [
    { title: 'Add your text', body: 'Paste your text or upload a document. Turn on Confidential mode if the file is sensitive.', callout: 'Paste or upload' },
    { title: 'Read your AI score', body: 'Get one overall score from Human to AI, with a short explanation of what is driving it.', callout: 'Overall AI score' },
    { title: 'See where AI shows up', body: 'The score distribution shows how much of your word count falls into each band, from confident AI to confident human.', callout: 'Score distribution' },
    { title: 'Check each paragraph and download', body: 'Review results paragraph by paragraph, then download an annotated PDF report that keeps your document structure.', callout: 'Paragraph results and PDF' }
  ];

  private readonly howSampleText = 'Effective time management is essential for academic success, as it allows students to balance coursework, extracurricular activities, and personal well-being.';
  private readonly howCycle = 95;

  // ---------------------------------------------------------------- state

  // hero
  heroPi = 0;
  heroPc: number = this.heroTypedWords[0].length;
  heroPm: 'type' | 'hold' | 'del' = 'hold';
  heroHold = 0;
  heroK = 0;
  heroBusy = false;
  heroBusyTicks = 0;
  heroDone = false;
  heroSi = -1;
  heroCustom: string | undefined = undefined;
  heroConf = false;
  heroGlow = false;

  // features
  featK = 0;
  featLz: number | undefined = undefined;
  featDl = 0;
  featDk = 0;

  // audience
  audIndex = 0;

  // how it works
  howK = 0;
  howSx: number | undefined = undefined;

  private tickTimer: any = null;
  private glowTimer: any = null;
  private featDocCache: { lz: number; list: FeatDocView[] } | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.tickTimer = setInterval(() => this.tick(), 60);
  }

  ngOnDestroy(): void {
    clearInterval(this.tickTimer);
    clearTimeout(this.glowTimer);
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---------------------------------------------------------------- ticker

  private tick(): void {
    this.tickHero();
    this.tickFeat();
    this.howK++;
  }

  private tickHero(): void {
    const word = this.heroTypedWords[this.heroPi];

    if (this.heroPm === 'type') {
      if (this.heroPc < word.length) {
        this.heroPc++;
      } else {
        this.heroPm = 'hold';
        this.heroHold = 0;
      }
    } else if (this.heroPm === 'hold') {
      const h = this.heroHold + 1;
      if (h > 34) {
        this.heroPm = 'del';
      } else {
        this.heroHold = h;
      }
    } else {
      if (this.heroPc > 0) {
        this.heroPc = Math.max(0, this.heroPc - 2);
      } else {
        this.heroPm = 'type';
        this.heroPi = (this.heroPi + 1) % this.heroTypedWords.length;
      }
    }

    this.heroK++;

    if (this.heroBusy) {
      const b = this.heroBusyTicks + 1;
      if (b > 22) {
        this.heroBusy = false;
        this.heroDone = true;
        this.heroBusyTicks = 0;
      } else {
        this.heroBusyTicks = b;
      }
    }
  }

  private tickFeat(): void {
    this.featK++;
    if (this.featDl === 1) {
      const d = this.featDk + 1;
      if (d > 24) {
        this.featDl = 2;
        this.featDk = 0;
      } else {
        this.featDk = d;
      }
    }
  }

  // ---------------------------------------------------------------- hero

  get heroTyped(): string {
    return this.heroTypedWords[this.heroPi].slice(0, this.heroPc);
  }

  get heroFlag(): boolean {
    return this.heroPm === 'hold' && this.heroHold > 9;
  }

  get heroHighlight(): number {
    return this.heroPm === 'hold' ? Math.min(100, this.heroHold * 12) : 0;
  }

  get heroIdle(): boolean {
    return !this.heroBusy && !this.heroDone;
  }

  get heroText(): string {
    if (this.heroCustom !== undefined) {
      return this.heroCustom;
    }
    return this.heroSi >= 0 ? this.heroSamples[this.heroSi].text : '';
  }

  get heroWords(): number {
    const text = this.heroText.trim();
    return text ? text.split(/\s+/).length : 0;
  }

  get heroSample(): HeroSample {
    return this.heroSamples[this.heroSi >= 0 ? this.heroSi : 1];
  }

  get heroMeta(): string {
    return this.heroConf
      ? this.heroWords + ' words · deleted after your check'
      : this.heroWords + '/500 words · 10 free checks today';
  }

  get heroRunLabel(): string {
    return this.heroConf ? 'Only on Confidential Data Plan' : 'Detect AI';
  }

  get techIndex(): number {
    return Math.floor(this.heroK / 30) % this.techniques.length;
  }

  get techNow(): string {
    const t = this.techniques[this.techIndex];
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  techSeparator(i: number): string {
    if (i === this.techniques.length - 1) {
      return '.';
    }
    return i === this.techniques.length - 2 ? ', and ' : ', ';
  }

  isHeroExampleOn(i: number): boolean {
    return this.heroSi === i && this.heroCustom === undefined;
  }

  pickHeroExample(i: number): void {
    this.heroSi = i;
    this.heroCustom = undefined;
    this.heroDone = false;
    this.heroBusy = false;
  }

  onHeroEdit(value: string): void {
    this.heroCustom = value;
    this.heroSi = -1;
  }

  toggleConf(): void {
    this.heroConf = !this.heroConf;
  }

  runHero(): void {
    if (this.heroConf || !this.heroWords) {
      return;
    }
    this.heroBusy = true;
    this.heroDone = false;
    this.heroBusyTicks = 0;
  }

  resetHero(): void {
    this.heroDone = false;
    this.heroBusy = false;
    this.heroSi = -1;
    this.heroCustom = undefined;
  }

  tryFree(): void {
    this.heroGlow = true;
    this.heroDone = false;
    this.heroBusy = false;
    clearTimeout(this.glowTimer);
    this.glowTimer = setTimeout(() => { this.heroGlow = false; }, 1800);
  }

  // ---------------------------------------------------------------- features

  get lensIndex(): number {
    return this.featLz !== undefined ? this.featLz : Math.floor(this.featK / 85) % this.lenses.length;
  }

  setLens(i: number): void {
    this.featLz = i;
  }

  prevLens(): void {
    this.featLz = (this.lensIndex + this.lenses.length - 1) % this.lenses.length;
  }

  nextLens(): void {
    this.featLz = (this.lensIndex + 1) % this.lenses.length;
  }

  get modelIndex(): number {
    return Math.floor(this.featK / 14) % this.models.length;
  }

  get featDocTitle(): string {
    return this.lensIndex === 5 ? 'AI Detection Report · nurse-retention-study.pdf' : 'nurse-retention-study.docx';
  }

  get featOverall(): string {
    return this.lensIndex === 1 ? 'Checked' : '58% AI';
  }

  get featDlShort(): string {
    if (this.featDl === 0) {
      return 'Download PDF';
    }
    return this.featDl === 1 ? 'Preparing…' : 'Saved';
  }

  startDownload(): void {
    if (this.featDl !== 1) {
      this.featDl = 1;
      this.featDk = 0;
    }
  }

  get featDoc(): FeatDocView[] {
    const lz = this.lensIndex;
    if (!this.featDocCache || this.featDocCache.lz !== lz) {
      this.featDocCache = { lz, list: this.buildFeatDoc(lz) };
    }
    return this.featDocCache.list;
  }

  private buildFeatDoc(lz: number): FeatDocView[] {
    return this.featDocSource.map((d) => {
      const ai = d.score > 50;
      let hl = false;
      let showTag = false;
      let tag = '';
      let border = 'transparent';
      let bg = 'transparent';

      if (lz === 0 && d.kind === 'humanized') { hl = true; showTag = true; tag = 'Humanized AI · ' + d.score + '%'; }
      if (lz === 3 && d.kind === 'para') { hl = true; showTag = true; tag = 'Paraphrased AI · ' + d.score + '%'; }
      if (lz === 2 || lz === 5) {
        showTag = true;
        tag = d.score + '% AI';
        border = this.bandColor(d.score);
        if (ai) { bg = '#FFF7ED'; }
      }
      if (lz === 4 && ai) { hl = true; showTag = true; tag = 'AI detected · ' + d.score + '%'; }

      return {
        text: d.text,
        orig: d.orig || '',
        showOrig: lz === 3 && d.kind === 'para',
        showTag,
        tag,
        ai,
        hl,
        border,
        bg
      };
    });
  }

  private bandColor(n: number): string {
    return n > 70 ? '#C2410C' : (n > 40 ? '#FB923C' : '#60A5FA');
  }

  // ---------------------------------------------------------------- audience

  get role(): AudRole {
    return this.roles[this.audIndex];
  }

  get audMode(): string {
    return this.audIndex === 3 ? 'CONFIDENTIAL MODE' : 'AI DETECTOR';
  }

  setRole(i: number): void {
    this.audIndex = i;
  }

  audColor(n: number): string {
    return n > 70 ? '#C2410C' : (n > 40 ? '#F59E0B' : '#16A34A');
  }

  // ---------------------------------------------------------------- how it works

  get howIndex(): number {
    return this.howSx !== undefined ? this.howSx : Math.floor(this.howK / this.howCycle) % this.howSteps.length;
  }

  get howProgress(): number {
    return this.howSx !== undefined ? 100 : Math.round((this.howK % this.howCycle) / this.howCycle * 100);
  }

  private get howTypedLength(): number {
    const text = this.howSampleText;
    return this.howIndex === 0 ? Math.min(text.length, Math.floor((this.howK % this.howCycle) * 2.4)) : text.length;
  }

  get howTyped(): string {
    if (this.howSx !== undefined && this.howIndex === 0) {
      return this.howSampleText;
    }
    return this.howSampleText.slice(0, this.howTypedLength);
  }

  get howDetectReady(): boolean {
    return (this.howSx !== undefined && this.howIndex === 0) || this.howTypedLength >= this.howSampleText.length;
  }

  setStep(i: number): void {
    this.howSx = i;
  }

  activeStep = 0;

  steps = [
    {
      number: '01',
      title: 'Upload once',
      heading: 'Upload once',
      description: 'Any PDF or Word file, up to 100 MB.',
      image: 'assets/images/ai-marketplace/dashboard1.png'
    },
    {
      number: '02',
      title: 'Pick your detectors',
      heading: 'Pick your detectors',
      description: 'Trinka, plus other leading detectors you trust.',
      image: 'assets/images/ai-marketplace/dashboard2.png'
    },
    {
      number: '03',
      title: 'Compare the results',
      heading: 'Compare the results',
      description: 'Every score side by side, on one scale.',
      image: 'assets/images/ai-marketplace/dashboard3.png'
    }
  ];

  setActiveStep(index: number): void {
    this.activeStep = index;
  }

}
